"use client";

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Ban,
  ChevronDown,
  CircleHelp,
  ShieldAlert,
} from "lucide-react";
import { Typography } from "@/components/common/ui/Typography";
import { cn } from "@/lib/utils/cn";
import type { BaseCarrier } from "../carrier-card/types";
import type { CarrierBucket, CarrierListProps, ColumnHeader, SortDir } from "./types";

function hideClassFor(hideBelow: ColumnHeader["hideBelow"]): string {
  switch (hideBelow) {
    case undefined:
      return "";
    case "lg":
      return "hidden lg:block";
    case "xl":
      return "hidden xl:block";
    case "2xl":
      return "hidden @2xl:block";
    case "3xl":
      return "hidden @3xl:block";
    case "4xl":
      return "hidden @4xl:block";
    case "5xl":
      return "hidden @5xl:block";
    case "6xl":
      return "hidden @6xl:block";
    default:
      return "";
  }
}

export function CarrierList<T extends BaseCarrier>({
  carriers,
  isLoading,
  columns,
  getSortValue,
  onSortedCarriersChange,
  defaultSortKey,
  defaultSortDir = "asc",
  getBucket,
  isErrorOrReferral,
  renderCard,
  renderLoadingCard,
  isCarrierLoading,
  renderErrorRow,
  renderNoQuoteRow,
  renderUnavailableRow,
  renderAllErrorsState,
  bucketLabels,
  errorBucketLabel,
}: CarrierListProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(defaultSortKey ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(defaultSortDir);
  const [errorsExpanded, setErrorsExpanded] = useState(false);
  const [noQuoteExpanded, setNoQuoteExpanded] = useState(false);
  const [questionsExpanded, setQuestionsExpanded] = useState(true);
  const [unavailableExpanded, setUnavailableExpanded] = useState(false);

  const applyDefaultSort = () => {
    setSortKey(defaultSortKey ?? null);
    setSortDir(defaultSortDir);
  };

  const handleSort = (key: string) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
      return;
    }
    if (key === defaultSortKey || sortDir === "asc") {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
      return;
    }
    applyDefaultSort();
  };

  const wasLoadingRef = useRef(isLoading);
  useEffect(() => {
    if (isLoading && !wasLoadingRef.current) {
      applyDefaultSort();
    }
    wasLoadingRef.current = isLoading;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, defaultSortKey, defaultSortDir]);

  const SortIcon = ({ col }: { col: string }) => {
    if (sortKey !== col)
      return (
        <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      );
    return sortDir === "asc" ? (
      <ArrowUp className="w-3 h-3" />
    ) : (
      <ArrowDown className="w-3 h-3" />
    );
  };

  // Bucket classification.
  const resolveBucket = (c: T): CarrierBucket => {
    if (getBucket) return getBucket(c);
    if (isErrorOrReferral && isErrorOrReferral(c)) return "error";
    return "main";
  };

  const mainCarriers = carriers.filter((c) => resolveBucket(c) === "main");
  const questionsCarriers = carriers.filter(
    (c) => resolveBucket(c) === "questions",
  );
  const noQuoteCarriers = carriers.filter(
    (c) => resolveBucket(c) === "no-quote",
  );
  const unavailableCarriers = carriers.filter(
    (c) => resolveBucket(c) === "unavailable",
  );
  const errorCarriers = carriers.filter((c) => resolveBucket(c) === "error");

  const sortedCarriers = useMemo(() => {
    if (!sortKey || !getSortValue) return mainCarriers;
    return [...mainCarriers].sort((a, b) => {
      const vA = getSortValue(a, sortKey);
      const vB = getSortValue(b, sortKey);
      const aMissing = vA === null || vA === undefined;
      const bMissing = vB === null || vB === undefined;
      if (aMissing || bMissing) {
        if (aMissing && bMissing) return 0;
        return aMissing ? 1 : -1;
      }
      let cmp: number;
      if (typeof vA === "string" && typeof vB === "string") {
        cmp = vA.localeCompare(vB);
      } else {
        cmp = (vA as number) - (vB as number);
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainCarriers, sortKey, sortDir, getSortValue]);

  const sortedIdStr = sortedCarriers.map((c) => c.id).join(",");
  useEffect(() => {
    onSortedCarriersChange?.(sortedIdStr.split(","));
  }, [sortedIdStr, onSortedCarriersChange]);

  // Show the "all errors" state when every enabled carrier landed in the error bucket.
  const allAreErrors =
    carriers.length > 0 && errorCarriers.length === carriers.length;

  useEffect(() => {
    if (allAreErrors && !isLoading) {
      setErrorsExpanded(true);
    }
  }, [allAreErrors, isLoading]);

  const colHeaderClass =
    "flex items-center gap-1 cursor-pointer group hover:text-gray-600 transition-colors uppercase";

  const questionsLabel =
    bucketLabels?.questions ??
    ((count: number) =>
      `${count} carrier${count !== 1 ? "s" : ""} with questions`);
  const noQuoteLabel =
    bucketLabels?.noQuote ??
    ((count: number) =>
      `${count} rejected/declined carrier${count !== 1 ? "s" : ""}`);
  const unavailableLabel =
    bucketLabels?.unavailable ??
    ((count: number) =>
      `${count} unavailable carrier${count !== 1 ? "s" : ""}`);
  const errorLabel =
    bucketLabels?.error ??
    errorBucketLabel ??
    ((count: number) =>
      `${count} carrier error${count !== 1 ? "s" : ""}`);

  return (
    <div className="@container overflow-x-auto">
      <div className="space-y-2 min-w-max">
        {allAreErrors && !isLoading && renderAllErrorsState?.()}

        {/* Column header row */}
        <div className="flex items-center gap-4 px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider select-none">
        <div className="w-5 flex-shrink-0" /> {/* checkbox spacer */}
        <div className="w-4 flex-shrink-0" /> {/* chevron spacer */}
        <div className="w-14 flex-shrink-0 hidden @3xl:block" />{" "}
        {/* logo spacer */}
        {columns.map((col) => {
          const hiddenClass = hideClassFor(col.hideBelow);
          const alignClass = col.align === "center" ? "text-center" : "";

          return (
            <Fragment key={col.key}>
              {col.spacerBefore && <div className="flex-1" />}
              <div
                className={`${col.width} flex-shrink-0 ${hiddenClass} ${alignClass}`}
              >
                {col.sortable ? (
                  <button
                    className={
                      colHeaderClass +
                      (col.align === "center" ? " justify-center w-full" : "")
                    }
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label} <SortIcon col={col.key} />
                  </button>
                ) : (
                  col.label
                )}
              </div>
            </Fragment>
          );
        })}
        {/* re-rate spacer (matches card's divider + button via display:contents) */}
        <div className="hidden @3xl:contents">
          <div className="w-px flex-shrink-0" />
          <div className="w-10 flex-shrink-0" />
        </div>
        </div>

        {/* Main bucket: carrier tiles */}
        {sortedCarriers.map((carrier) => {
          if (isCarrierLoading?.(carrier) && renderLoadingCard) {
            return <div key={carrier.id}>{renderLoadingCard(carrier)}</div>;
          }
          return <div key={carrier.id}>{renderCard(carrier)}</div>;
        })}

        {/* Questions bucket */}
        {questionsCarriers.length > 0 && (
        <div className="mt-2">
          <button
            onClick={() => setQuestionsExpanded((prev) => !prev)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ChevronDown
              className={cn(
                "w-4 h-4 transition-transform duration-200",
                questionsExpanded ? "rotate-0" : "-rotate-90",
              )}
            />
            <CircleHelp className="w-3.5 h-3.5 text-slate-500" />
            <Typography
              as="span"
              variant="body"
              weight="medium"
              color="inherit"
            >
              {questionsLabel(questionsCarriers.length)}
            </Typography>
          </button>

          {questionsExpanded && (
            <div className="mt-3 space-y-2">
              {questionsCarriers.map((carrier) => {
                if (isCarrierLoading?.(carrier) && renderLoadingCard) {
                  return <div key={carrier.id}>{renderLoadingCard(carrier)}</div>;
                }
                return <div key={carrier.id}>{renderCard(carrier)}</div>;
              })}
            </div>
          )}
        </div>
        )}

        {/* No-Quote bucket (declined / rejected / $0 referrals) */}
        {noQuoteCarriers.length > 0 && renderNoQuoteRow && (
        <div className="mt-2">
          <button
            onClick={() => setNoQuoteExpanded((prev) => !prev)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ChevronDown
              className={cn(
                "w-4 h-4 transition-transform duration-200",
                noQuoteExpanded ? "rotate-0" : "-rotate-90",
              )}
            />
            <Ban className="w-3.5 h-3.5 text-orange-500" />
            <Typography
              as="span"
              variant="body"
              weight="medium"
              color="inherit"
            >
              {noQuoteLabel(noQuoteCarriers.length)}
            </Typography>
          </button>

          {noQuoteExpanded && (
            <div className="mt-3 space-y-2">
              {noQuoteCarriers.map((carrier) => (
                <div key={carrier.id}>{renderNoQuoteRow(carrier)}</div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Unavailable bucket */}
        {unavailableCarriers.length > 0 && renderUnavailableRow && (
        <div className="mt-2">
          <button
            onClick={() => setUnavailableExpanded((prev) => !prev)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ChevronDown
              className={cn(
                "w-4 h-4 transition-transform duration-200",
                unavailableExpanded ? "rotate-0" : "-rotate-90",
              )}
            />
            <ShieldAlert className="w-3.5 h-3.5 text-slate-500" />
            <Typography
              as="span"
              variant="body"
              weight="medium"
              color="inherit"
            >
              {unavailableLabel(unavailableCarriers.length)}
            </Typography>
          </button>

          {unavailableExpanded && (
            <div className="mt-3 space-y-2">
              {unavailableCarriers.map((carrier) => (
                <div key={carrier.id}>{renderUnavailableRow(carrier)}</div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Errors bucket */}
        {errorCarriers.length > 0 && renderErrorRow && (
        <div className="mt-2">
          <button
            onClick={() => setErrorsExpanded((prev) => !prev)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ChevronDown
              className={cn(
                "w-4 h-4 transition-transform duration-200",
                errorsExpanded ? "rotate-0" : "-rotate-90",
              )}
            />
            <AlertCircle className="w-3.5 h-3.5 text-red-500" />
            <Typography
              as="span"
              variant="body"
              weight="medium"
              color="inherit"
            >
              {errorLabel(errorCarriers.length)}
            </Typography>
          </button>

          {errorsExpanded && (
            <div className="mt-3 space-y-2">
              {errorCarriers.map((carrier) => (
                <div key={carrier.id}>{renderErrorRow(carrier)}</div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
